import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle, XCircle, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useLocale } from "@/contexts/LocaleContext";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";

const Invite = () => {
  const { token } = useParams<{ token: string }>();
  const { user, loading: authLoading } = useAuth();
  const { locale } = useLocale();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState<"loading" | "success" | "error" | "expired" | "auth_required">("loading");
  const [eventTitle, setEventTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const processInvite = async () => {
      if (!token) {
        setStatus("error");
        setErrorMessage(locale.language === "pt" ? "Token de convite inválido" : "Invalid invite token");
        return;
      }

      // Check if user is logged in
      if (!authLoading && !user) {
        setStatus("auth_required");
        return;
      }

      if (authLoading) return;

      try {
        // Fetch invite details using secure function
        const { data: inviteData, error: inviteError } = await supabase
          .rpc("get_invite_by_token", { _token: token });

        if (inviteError || !inviteData || inviteData.length === 0) {
          setStatus("error");
          setErrorMessage(locale.language === "pt" ? "Convite não encontrado" : "Invite not found");
          return;
        }

        const invite = inviteData[0];

        // Check if invite is expired
        if (invite.expires_at && new Date(invite.expires_at) < new Date()) {
          setStatus("expired");
          return;
        }

        // Check if max uses reached
        if (invite.max_uses && invite.use_count >= invite.max_uses) {
          setStatus("expired");
          return;
        }

        // Fetch event details separately
        const { data: event, error: eventError } = await supabase
          .from("events")
          .select("title, title_pt")
          .eq("id", invite.event_id)
          .maybeSingle();

        if (event) {
          setEventTitle(locale.language === "pt" && event.title_pt ? event.title_pt : event.title);
        }

        // Check if user already joined
        const { data: existingParticipant } = await supabase
          .from("event_participants")
          .select("id")
          .eq("event_id", invite.event_id)
          .eq("user_id", user!.id)
          .single();

        if (existingParticipant) {
          // Already a participant, redirect to event
          setStatus("success");
          return;
        }

        // Add user as participant
        const { error: participantError } = await supabase
          .from("event_participants")
          .insert({
            event_id: invite.event_id,
            user_id: user!.id,
            invite_id: invite.id,
          });

        if (participantError) throw participantError;

        // Increment use count
        await supabase
          .from("event_invites")
          .update({ use_count: invite.use_count + 1 })
          .eq("id", invite.id);

        setStatus("success");
      } catch (error: any) {
        console.error("Error processing invite:", error);
        setStatus("error");
        setErrorMessage(error.message);
      }
    };

    processInvite();
  }, [token, user, authLoading, locale.language]);

  const handleLoginRedirect = () => {
    // Store invite token to process after login
    sessionStorage.setItem("pendingInvite", token || "");
    navigate("/auth");
  };

  const handleViewEvents = () => {
    navigate("/#events");
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 pt-32 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto"
        >
          <div className="glass-card p-8 text-center">
            {status === "loading" && (
              <>
                <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-primary" />
                <h2 className="text-xl font-bold text-foreground mb-2">
                  {locale.language === "pt" ? "Processando convite..." : "Processing invite..."}
                </h2>
              </>
            )}

            {status === "auth_required" && (
              <>
                <Lock className="w-12 h-12 mx-auto mb-4 text-primary" />
                <h2 className="text-xl font-bold text-foreground mb-2">
                  {locale.language === "pt" ? "Login necessário" : "Login required"}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {locale.language === "pt" 
                    ? "Você precisa estar logado para aceitar este convite" 
                    : "You need to be logged in to accept this invite"}
                </p>
                <Button variant="hero" onClick={handleLoginRedirect} className="w-full">
                  {locale.language === "pt" ? "Fazer Login" : "Log In"}
                </Button>
              </>
            )}

            {status === "success" && (
              <>
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                <h2 className="text-xl font-bold text-foreground mb-2">
                  {locale.language === "pt" ? "Convite aceito!" : "Invite accepted!"}
                </h2>
                <p className="text-muted-foreground mb-2">
                  {locale.language === "pt" 
                    ? "Você agora pode participar do evento:" 
                    : "You can now participate in the event:"}
                </p>
                <p className="text-foreground font-medium mb-6">{eventTitle}</p>
                <Button variant="hero" onClick={handleViewEvents} className="w-full">
                  {locale.language === "pt" ? "Ver Eventos" : "View Events"}
                </Button>
              </>
            )}

            {status === "expired" && (
              <>
                <XCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
                <h2 className="text-xl font-bold text-foreground mb-2">
                  {locale.language === "pt" ? "Convite expirado" : "Invite expired"}
                </h2>
                <p className="text-muted-foreground mb-6">
                  {locale.language === "pt" 
                    ? "Este convite não é mais válido" 
                    : "This invite is no longer valid"}
                </p>
                <Button variant="outline" onClick={handleViewEvents} className="w-full">
                  {locale.language === "pt" ? "Ver Eventos Públicos" : "View Public Events"}
                </Button>
              </>
            )}

            {status === "error" && (
              <>
                <XCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
                <h2 className="text-xl font-bold text-foreground mb-2">
                  {locale.language === "pt" ? "Erro" : "Error"}
                </h2>
                <p className="text-muted-foreground mb-6">{errorMessage}</p>
                <Button variant="outline" onClick={handleViewEvents} className="w-full">
                  {locale.language === "pt" ? "Voltar" : "Go Back"}
                </Button>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Invite;
